import { useEffect, useContext, useState, useCallback, Suspense, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";

import HealthCardActual from "./healthCardActual/HealthCardActual";
import IntercityHealthCardPrint from "./BopHealthPlanCardPrint/BopHealthCardPrint";
import { api } from "../../../../utils/api";
import CustomEmailDialog from "./customEnterEmailDialog/CustomEmailDialog";
import { ThemeContext, AuthContext } from "../../../../contexts";

import "./BOPHealthCard.css";
import TogglePlanCards from "./TogglePlanCards/TogglePlanCards";
import BopHealthPlanPrint from "./BopHealthPlanCardPrint/BopHealthCardPrint";
import BopDentalCardPrint from "./BopDentalCardPrint/BopDentalCardPrint";
import BopVisionCardPrint from "./BopVisionCardPrint/BopVisionCardPrint";

const BOPHealthCard = () => {
	const [selectedCard, setSelectedCard] = useState<"health" | "dental" | "vision">("health");
	const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
	const [hasHealthPlan, setHasHealthPlan] = useState<boolean>(false);
	const [hasDentalPlan, setHasDentalPlan] = useState<boolean>(false);
	const [hasVisionPlan, setHasVisionPlan] = useState<boolean>(false);
	const [healthPlanDetails, setHealthPlanDetails] = useState<any>(null);
	const [enteredEmail, setEnteredEmail] = useState("");
	const [customEmailFormDialogProps, setCustomEmailFormDialogProps] = useState({
		openDialog: false,
		title: "Email Health Card",
		actions: [
			{
				label: "Cancel",
				callback: () => {
					setCustomEmailFormDialogProps(Object.assign({}, customEmailFormDialogProps, { openDialog: false }));
				}
			}
		]
	});

	const { theme } = useContext(ThemeContext);
	const { member } = useContext(AuthContext);
	const componentRef = useRef(null);
	const handlePrint = useReactToPrint({
		content: () => componentRef.current
	});
	const handleCardChange = (card: "health" | "dental" | "vision") => {
		setSelectedCard(card); // Update parent state
	};

	const speedDialActions = [
		{
			name: <div className="tooltip-speed-dial">Download</div>,
			icon: <DownloadIcon />,
			actionName: "download_brochure"
		},
		{
			name: <div className="tooltip-speed-dial">Email</div>,
			icon: <AttachEmailIcon />,
			actionName: "email_brochure"
		}
	];

	const handleOpenSpeedDial = () => setOpenSpeedDial(true);

	const handleCloseSpeedDial = () => setOpenSpeedDial(false);

	// eslint-disable-next-line arrow-parens
	const handleEnterEmailChange = useCallback(
		// eslint-disable-next-line arrow-parens
		(event) => {
			const { value } = event.target;
			setEnteredEmail(value);
		},
		[]
	);

	const emailHealthCardHandler = useCallback(async () => {
		const emailHealthcardQuery = {
			email: enteredEmail,
			group_number: member.group_number,
			plan_name: selectedCard
		};
		const response = await api.healthCard.emailHealthCard(emailHealthcardQuery);
		alert(response);
		setCustomEmailFormDialogProps(Object.assign({}, customEmailFormDialogProps, { openDialog: false }));
	}, [customEmailFormDialogProps, enteredEmail, member]);

	const getMemberEmail = useCallback(() => {
		if (member && (member.email !== "" || member.email !== null || member.email !== undefined)) {
			if (member.email !== "email@email.com") setEnteredEmail(member.email);
		} else {
			setEnteredEmail("");
		}
	}, [member]);

	const handleOpenEnterEmailDialog = useCallback(() => {
		setCustomEmailFormDialogProps(Object.assign({}, customEmailFormDialogProps, { openDialog: true }));
	}, [customEmailFormDialogProps]);

	const getActiveHealthPlan = useCallback(async () => {
		const healthPlan = await api.enrollment.getActiveHealthPlan();
		// console.log("HEALTHPLANRESPONSECHECK---->", healthPlan);
		if (healthPlan) {
			setHasHealthPlan(true);
			setHealthPlanDetails(healthPlan);
		} else {
			setHasHealthPlan(false);
		}
	}, []);

	const getActiveDentalPlan = useCallback(async () => {
		const dentalPlan = await api.enrollment.getActiveDentalPlan();
		// console.log("DENTALPLANRESPONSECHECK---->", dentalPlan);
		if (dentalPlan) {
			setHasDentalPlan(true);
			// setDentalPlanDetails(dentalPlan);
		} else {
			setHasDentalPlan(false);
			// setDentalPlanDetails(null);
		}
	}, []);

	const getActiveVisionPlan = useCallback(async () => {
		const visionPlan = await api.enrollment.getActiveVisionPlan();
		// console.log("VISIONPLANRESPONSECHECK---->", visionPlan);
		if (visionPlan) {
			setHasVisionPlan(true);
			// setDentalPlanDetails(dentalPlan);
		} else {
			setHasVisionPlan(false);
			// setDentalPlanDetails(null);
		}
	}, []);

	useEffect(() => {
		if (selectedCard === "health") {
			getActiveHealthPlan();
		} else if (selectedCard === "dental") {
			getActiveDentalPlan();
		} else {
			getActiveVisionPlan();
		}
	}, [selectedCard, getActiveHealthPlan, getActiveDentalPlan, getActiveVisionPlan]);

	useEffect(() => {
		if (member) {
			getMemberEmail();
		}
	}, [getMemberEmail, member]);

	return (
		<>
			<Suspense fallback={<div />}>
				<CustomEmailDialog
					customEmailFormDialogProps={customEmailFormDialogProps}
					enteredEmail={enteredEmail}
					setEnteredEmail={setEnteredEmail}
					emailHealthCardHandler={emailHealthCardHandler}
					// handleEnterEmailChange={handleEnterEmailChange}
				/>
			</Suspense>
			{(selectedCard === "health" && hasHealthPlan) ||
			(selectedCard === "dental" && hasDentalPlan) ||
			(selectedCard === "vision" && hasVisionPlan) ? (
				<SpeedDial
					ariaLabel="SpeedDial tooltip example"
					className="speed-dial"
					icon={<SpeedDialIcon />}
					sx={{ position: "fixed", bottom: 16, right: 16 }}
					onClose={handleCloseSpeedDial}
					onOpen={handleOpenSpeedDial}
					open={openSpeedDial}
					FabProps={{
						style: {
							backgroundColor: theme?.primary_color
						}
					}}
				>
					{speedDialActions.map((action, index) => {
						const { name, icon, actionName } = action;
						return (
							<SpeedDialAction
								key={index}
								icon={icon}
								tooltipTitle={name}
								tooltipOpen
								onClick={actionName === "download_brochure" ? handlePrint : handleOpenEnterEmailDialog}
							/>
						);
					})}
				</SpeedDial>
			) : null}

			{/* <HealthCardActual /> */}
			<TogglePlanCards
				healthPlanDetails={healthPlanDetails}
				onCardChange={handleCardChange}
				hasHealthPlan={hasHealthPlan}
				hasDentalPlan={hasDentalPlan}
				hasVisionPlan={hasVisionPlan}
			/>
			<div style={{ display: "none" }}>
				<Suspense fallback={<div />}>
					{member ? (
						selectedCard === "health" ? (
							<BopHealthPlanPrint
								{...member}
								ref={componentRef}
								first_name={member.first_name.toUpperCase()}
								last_name={member.last_name.toUpperCase()}
								_ssn={member?.SSN}
								group_number={member.group_number.toString()}
								healthPlanDetails={healthPlanDetails}
							/>
						) : selectedCard === "dental" ? (
							<BopDentalCardPrint
								{...member}
								ref={componentRef}
								first_name={member.first_name.toUpperCase()}
								last_name={member.last_name.toUpperCase()}
								_ssn={member?.SSN}
								group_number={member.group_number.toString()}
								healthPlanDetails={healthPlanDetails}
							/>
						) : selectedCard === "vision" ? (
							<BopVisionCardPrint
								{...member}
								ref={componentRef}
								first_name={member.first_name.toUpperCase()}
								last_name={member.last_name.toUpperCase()}
								_ssn={member?.SSN}
								group_number={member.group_number.toString()}
								healthPlanDetails={healthPlanDetails}
							/>
						) : null
					) : null}
				</Suspense>
			</div>
		</>
	);
};

export default BOPHealthCard;
